import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Copy } from "lucide-react";
import {
  Plus,
  Save,
  Trash2,
  Pencil,
  X,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Wrench,
  FileSignature,
  Eye as EyeIcon,
  GraduationCap,
  ListChecks,
  Upload,
  Calendar,
  ChevronDown,
  Search,
  Link2,
  MousePointerClick,
  AlertCircle,
  Sparkles,
  GripVertical,
  Camera,
  Plane,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ApertureLoader from "@/components/ApertureLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BUTTON_PRESETS,
  BUTTON_COLORS,
  buttonColorClasses,
  iconForTitle,
  normalizeButton,
  stripWwwFromUrl,
  presetFor,
} from "@/lib/checklistButtons";

const PHASES = {
  documents: { label: "Documents", description: "Contracts, handbooks, style standards." },
  exam: { label: "Final Exam", description: "Confirms you've absorbed the handbook and standards." },
  tax_payments: { label: "Tax & Payments", description: "Getting paid on time." },
  scheduling: { label: "Schedule Your Call", description: "Final step — book a time with the Reelty team. Unlocks after everything else is complete." },
};

const PHASE_ICON = {
  documents: FileText,
  exam: GraduationCap,
  tax_payments: Wrench,
  scheduling: Calendar,
};

// Matches ACTION_CTA in components/photographer/constants.js — kept in sync
// so the "Photographer Preview" in this admin page shows the EXACT label the
// photographer will see for each action type. Client rule: sign/upload items
// promote the document download to the primary button (oxblood) and demote
// the actual upload to a ghost secondary. Update both sides together.
const ACTION_CTA_LABEL = {
  review: "View Document",
  sign: "Download & Sign",
  setup: "Complete Setup",
  exam: "Take Exam",
  upload: "Download",
  schedule: "Book Call",
};

const ACTION_META = {
  review: { label: "Review", icon: EyeIcon, hint: "Photographer just needs to read it." },
  sign: { label: "Signature", icon: FileSignature, hint: "Photographer must sign externally (e.g. Docsend), and optionally upload the signed copy." },
  upload: { label: "Upload", icon: Upload, hint: "Photographer uploads a file directly (PDF, JPG, or PNG · up to 25 MB). Marks complete on upload." },
  setup: { label: "Setup", icon: Wrench, hint: "Photographer completes a setup step (e.g. bank details)." },
  exam: { label: "Exam", icon: GraduationCap, hint: "Web-based exam inside the portal — scored automatically." },
  schedule: { label: "Schedule", icon: Calendar, hint: "Photographer books a call via a Calendly link. This is the FINAL step and only unlocks after everything else is done." },
};

// Given whatever the admin pasted into the schedule item's URL field, extract
// the Calendly `data-url`. Accepts either the full inline-widget snippet or
// a bare URL. Preserves query params like `?primary_color=7e1119`.
function _extractCalendlyUrl(raw) {
  if (!raw) return "";
  const trimmed = raw.trim();
  const match = trimmed.match(/data-url\s*=\s*["']([^"']+)["']/i);
  if (match) return match[1].trim();
  return trimmed;
}

const LABEL_PRESETS = [
  "Upload", "Sign", "View", "Download", "Schedule",
  "Book Call", "Take Exam", "Set Up", "Learn More", "View Details",
];

function LabelWithPresets({ value, onChange, placeholder, testId }) {
  return (
    <div className="mt-1.5 space-y-1.5">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] text-sm"
      />
      <div className="flex flex-wrap gap-1">
        {LABEL_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            data-testid={`${testId}-preset-${preset.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wide transition-colors ${
              value === preset
                ? "bg-[#6B0F1A] border-[#6B0F1A] text-[#F5EBD9]"
                : "bg-white border-[#1A0F0A]/15 text-[#4D3F35] hover:bg-[#1A0F0A]/5"
            }`}
          >
            {preset}
          </button>
        ))}
      </div>
    </div>
  );
}

const CHECKLIST_ROLES = [
  { key: "photographer", label: "Photographer", icon: Camera,    blurb: "Real-estate stills onboarding" },
  { key: "pilot",        label: "Pilot",        icon: Plane,     blurb: "FAA Part 107 drone onboarding" },
  { key: "employee",     label: "Employee",     icon: Briefcase, blurb: "Reelty internal onboarding" },
];

const ROLE_HEADLINE = {
  photographer: "Every photographer sees these steps in this exact order. Click any row to see how it renders in the portal.",
  pilot:        "Every pilot sees these steps in this exact order. Click any row to see how it renders in the portal.",
  employee:     "Every Reelty employee sees these steps in this exact order. Click any row to see how it renders in the portal.",
};

const emptyDraft = (role = "photographer") => ({
  role,
  title: "",
  description: "",
  phase: "documents",
  action_type: "review",
  external_url: "",
  primary_label: "",
  secondary_label: "",
  secondary_url: "",
  buttons: [],
  order: 100,
  is_active: true,
});

function ItemForm({ value, onChange, onSubmit, onCancel, submitting, isNew }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const ActionIcon = ACTION_META[value.action_type]?.icon || EyeIcon;
  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4"
      data-testid={isNew ? "new-item-form" : `edit-item-form-${value.id}`}
    >
      <div>
        <Label className="font-mono-tag text-[10px] text-[#78584B]">TITLE</Label>
        <Input
          data-testid="template-title-input"
          value={value.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="Photographer Handbook"
          required
          className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
        />
      </div>

      <div>
        <Label className="font-mono-tag text-[10px] text-[#78584B]">DESCRIPTION</Label>
        <Textarea
          data-testid="template-description-input"
          value={value.description || ""}
          onChange={(e) => set("description", e.target.value)}
          placeholder="What is this document / what should the photographer do?"
          rows={2}
          className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
        />
      </div>

      {/* Schedule items still need their Calendly embed field — the inline
          widget is mounted from this URL, not from a button click. For every
          other action type the URL now lives on the individual buttons; we
          intentionally hide the legacy "External URL" input so it's clear
          that buttons are the sole source of the destination. */}
      {value.action_type === "schedule" && (
        <div>
          <Label className="font-mono-tag text-[10px] text-[#78584B]">
            CALENDLY EMBED SNIPPET
          </Label>
          <div className="mt-1.5 space-y-2">
            <Textarea
              data-testid="template-calendly-embed-input"
              value={value.external_url || ""}
              onChange={(e) => set("external_url", _extractCalendlyUrl(e.target.value))}
              placeholder={'<!-- Calendly inline widget begin -->\n<div class="calendly-inline-widget" data-url="https://calendly.com/goreelty/photographer-onboarding-session?background_color=fffff0&primary_color=7e1119" style="min-width:320px;height:max;"></div>\n<script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async></script>\n<!-- Calendly inline widget end -->'}
              rows={6}
              className="bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] font-mono text-[11px]"
            />
            <div className="text-[10px] font-mono-tag text-[#78584B] leading-relaxed">
              Paste the full snippet from Calendly (Share → Embed → Inline). We only store the
              <code className="text-[#6B0F1A]"> data-url</code> — the widget script is already
              loaded globally in the portal.
            </div>
            {value.external_url && (
              <div className="bg-[#F5EBD9] rounded-sm p-2 border border-[#1A0F0A]/10">
                <div className="font-mono-tag text-[9px] text-[#78584B] mb-1">EXTRACTED URL</div>
                <div className="text-[11px] text-[#6B0F1A] break-all font-mono">
                  {value.external_url}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="font-mono-tag text-[10px] text-[#78584B]">PHASE</Label>
          <Select value={value.phase} onValueChange={(v) => set("phase", v)}>
            <SelectTrigger
              data-testid="template-phase-select"
              className="mt-1.5 bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A]">
              {Object.entries(PHASES).map(([k, meta]) => (
                <SelectItem key={k} value={k}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-mono-tag text-[10px] text-[#78584B]">ACTION</Label>
          <Select value={value.action_type} onValueChange={(v) => set("action_type", v)}>
            <SelectTrigger
              data-testid="template-action-select"
              className="mt-1.5 bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A]">
              {Object.entries(ACTION_META).map(([k, meta]) => (
                <SelectItem key={k} value={k}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 text-[11px] text-[#78584B] leading-relaxed">
        <ActionIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
        {ACTION_META[value.action_type]?.hint}
      </div>

      {/* Custom buttons configurator — full control over count, label, color,
          action (URL vs Upload), and URL per button. When at least one button
          is defined, the legacy primary_label / secondary_label rendering is
          bypassed entirely on the photographer card. */}
      <div className="pt-4 border-t border-[#1A0F0A]/10 space-y-3">
        <div className="flex items-baseline justify-between">
          <Label className="font-mono-tag text-[10px] text-[#78584B]">
            BUTTONS
          </Label>
          <span className="font-mono-tag text-[9px] text-[#78584B]">
            {(value.buttons || []).length} configured
          </span>
        </div>
        <p className="text-[11px] text-[#78584B] leading-relaxed -mt-2">
          Add exactly the buttons the photographer should see for this item — no more, no less.
          Pick a title (icon is chosen automatically), a color, and either a link or the file-upload action.
        </p>

        <div className="space-y-2" data-testid="template-buttons-list">
          {(value.buttons || []).map((btn, idx) => {
            const preset = presetFor(btn.title);
            const BtnIcon = iconForTitle(btn.title);
            const isUploadAction = btn.action === "upload";
            const setBtn = (patch) => {
              const next = [...(value.buttons || [])];
              next[idx] = { ...next[idx], ...patch };
              onChange({ ...value, buttons: next });
            };
            const removeBtn = () => {
              const next = [...(value.buttons || [])];
              next.splice(idx, 1);
              onChange({ ...value, buttons: next });
            };
            const moveBtn = (delta) => {
              const next = [...(value.buttons || [])];
              const target = idx + delta;
              if (target < 0 || target >= next.length) return;
              [next[idx], next[target]] = [next[target], next[idx]];
              onChange({ ...value, buttons: next });
            };
            return (
              <div
                key={btn._key ?? idx}
                className="rounded-sm border border-[#1A0F0A]/12 bg-white p-3 space-y-2"
                data-testid={`template-button-row-${idx}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-sm bg-[#6B0F1A]/8 border border-[#6B0F1A]/15 flex items-center justify-center shrink-0">
                    <BtnIcon className="w-4 h-4 text-[#6B0F1A]" strokeWidth={1.75} />
                  </div>
                  {/* Title (preset dropdown OR custom) */}
                  <div className="flex-1 min-w-0">
                    <Select
                      value={preset ? btn.title : "__custom__"}
                      onValueChange={(v) => {
                        if (v === "__custom__") {
                          setBtn({ title: preset ? "" : btn.title });
                          return;
                        }
                        const p = BUTTON_PRESETS.find((x) => x.title === v);
                        // Choosing a preset also seeds action + color for
                        // convenience — admin can still tweak after.
                        setBtn({
                          title: v,
                          action: p?.defaultAction || btn.action || "url",
                          color: btn.color || p?.defaultColor || "primary",
                        });
                      }}
                    >
                      <SelectTrigger
                        data-testid={`template-button-title-${idx}`}
                        className="h-8 bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm text-sm"
                      >
                        <SelectValue placeholder="Choose a title…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A]">
                        {BUTTON_PRESETS.map((p) => (
                          <SelectItem key={p.title} value={p.title}>
                            {p.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom__">Custom title…</SelectItem>
                      </SelectContent>
                    </Select>
                    {!preset && (
                      <Input
                        value={btn.title || ""}
                        onChange={(e) => setBtn({ title: e.target.value })}
                        placeholder="Custom button title"
                        data-testid={`template-button-title-custom-${idx}`}
                        className="mt-1.5 h-8 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] text-sm"
                      />
                    )}
                  </div>

                  {/* Color swatches */}
                  <div className="flex items-center gap-1 shrink-0">
                    {BUTTON_COLORS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setBtn({ color: c.key })}
                        data-testid={`template-button-color-${idx}-${c.key}`}
                        title={c.label}
                        aria-label={`${c.label} color`}
                        className={`w-6 h-6 rounded-sm border-2 transition-all ${
                          btn.color === c.key
                            ? "border-[#1A0F0A] scale-110"
                            : "border-[#1A0F0A]/20 hover:border-[#1A0F0A]/40"
                        }`}
                        style={{
                          background:
                            c.key === "secondary"
                              ? "repeating-linear-gradient(45deg,#F5EBD9,#F5EBD9 4px,#E8DBC1 4px,#E8DBC1 6px)"
                              : c.swatch,
                        }}
                      />
                    ))}
                  </div>

                  <div className="flex flex-col items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => moveBtn(-1)}
                      disabled={idx === 0}
                      data-testid={`template-button-up-${idx}`}
                      className="p-0.5 text-[#78584B] hover:text-[#6B0F1A] disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBtn(1)}
                      disabled={idx === (value.buttons || []).length - 1}
                      data-testid={`template-button-down-${idx}`}
                      className="p-0.5 text-[#78584B] hover:text-[#6B0F1A] disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={removeBtn}
                    data-testid={`template-button-remove-${idx}`}
                    className="p-1 text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 rounded-sm shrink-0"
                    title="Remove this button"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>

                {/* Action + URL row */}
                <div className="flex items-center gap-2 pl-10">
                  <div className="flex rounded-sm border border-[#1A0F0A]/15 overflow-hidden text-[10px] font-mono-tag">
                    <button
                      type="button"
                      onClick={() => setBtn({ action: "url" })}
                      data-testid={`template-button-action-url-${idx}`}
                      className={`px-2 py-1 transition-colors ${
                        btn.action === "url"
                          ? "bg-[#6B0F1A] text-[#F5EBD9]"
                          : "bg-white text-[#4D3F35] hover:bg-[#1A0F0A]/5"
                      }`}
                    >
                      LINK
                    </button>
                    <button
                      type="button"
                      onClick={() => setBtn({ action: "upload" })}
                      data-testid={`template-button-action-upload-${idx}`}
                      className={`px-2 py-1 border-l border-[#1A0F0A]/15 transition-colors ${
                        btn.action === "upload"
                          ? "bg-[#6B0F1A] text-[#F5EBD9]"
                          : "bg-white text-[#4D3F35] hover:bg-[#1A0F0A]/5"
                      }`}
                    >
                      UPLOAD
                    </button>
                  </div>
                  {!isUploadAction && (
                    <div className="relative flex-1">
                      <ExternalLink className="w-3.5 h-3.5 text-[#78584B] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                      <Input
                        value={btn.url || ""}
                        onChange={(e) => setBtn({ url: stripWwwFromUrl(e.target.value) })}
                        placeholder="https://…"
                        type="url"
                        data-testid={`template-button-url-${idx}`}
                        className="h-8 pl-8 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] text-xs"
                      />
                    </div>
                  )}
                  {isUploadAction && (
                    <span className="text-[10px] font-mono-tag text-[#78584B]">
                      Opens the file picker for the photographer.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <Button
          type="button"
          onClick={() => {
            const seeded = normalizeButton({
              title: "Upload",
              color: "primary",
              action: "upload",
              url: "",
            });
            onChange({ ...value, buttons: [...(value.buttons || []), seeded] });
          }}
          variant="ghost"
          data-testid="template-add-button-btn"
          className="h-8 rounded-sm text-[11px] font-bold tracking-wide inline-flex items-center gap-1.5 bg-transparent text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-dashed border-[#6B0F1A]/40"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          Add button
        </Button>

        {(value.buttons || []).length === 0 && (
          <div className="text-[10px] font-mono-tag text-[#78584B] leading-relaxed">
            No buttons configured — the photographer card will fall back to the action-type default primary button plus the auto Upload button for sign/upload items. Add at least one button here to take full control.
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="template-save-btn"
          className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
          {submitting ? "Saving…" : isNew ? "Create item" : "Save changes"}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="ghost"
          data-testid="template-cancel-btn"
          className="text-[#78584B] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px]"
        >
          <X className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Collapsible row for a single template item — Apple-style disclosure.
 * Collapsed state shows title + action pill + config-glance chips + chevron.
 * Expanded state reveals description, live "photographer sees this" preview,
 * URL, reorder controls, and the full edit form inline (no modal, no page nav).
 * The whole card is clickable to expand; nested buttons stopPropagation so
 * they don't accidentally toggle the panel.
 */
function TemplateRow({
  item,
  idx,
  siblingCount,
  isExpanded,
  isEditing,
  onToggleExpanded,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  editDraft,
  onEditDraftChange,
  savingEdit,
  onMove,
  onToggleActive,
  onRemove,
  onDuplicateTo,
  activeRole,
}) {
  const ActionIcon = ACTION_META[item.action_type]?.icon || EyeIcon;
  const hasUrl = !!item.external_url;
  const hasCustomButtons = Array.isArray(item.buttons) && item.buttons.length > 0;
  const hasSecondary = !!(item.secondary_label && item.secondary_url);
  const hasPrimaryLabel = !!item.primary_label;
  // Exams/uploads don't require an external URL, so don't scold admins for those.
  // Also skip the "needs URL" nag when the item has been fully replaced with a
  // custom buttons array (the legacy `external_url` field is irrelevant then).
  const requiresUrl = !["upload", "exam"].includes(item.action_type) && !hasCustomButtons;
  const needsAttention = requiresUrl && !hasUrl && item.is_active;
  const primaryButtonText = item.primary_label || ACTION_CTA_LABEL[item.action_type] || "View";

  const stop = (e) => e.stopPropagation();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid={`template-row-${item.id}`}
      className={`card-surface rounded-sm overflow-hidden transition-all ${
        !item.is_active ? "opacity-60" : ""
      } ${isExpanded ? "border-[#6B0F1A]/40 shadow-[0_6px_20px_-8px_rgba(107,15,26,0.35)]" : "hover:border-[#6B0F1A]/30 hover:translate-x-0.5"} ${
        needsAttention && !isExpanded ? "ring-1 ring-[#B08D57]/30" : ""
      }`}
    >
      {/* COLLAPSED / HEADER — always visible, click anywhere to toggle.
          Uses a role="button" div (not a real <button>) because we nest real
          <button> elements inside for reorder controls, and nested buttons
          are invalid HTML — the browser hoists inner buttons out of the outer
          one, which silently breaks click handling on the entire row. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggleExpanded(item.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleExpanded(item.id);
          }
        }}
        aria-expanded={isExpanded}
        data-testid={`template-row-header-${item.id}`}
        className="w-full text-left p-4 flex items-center gap-4 group cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A]/40"
      >
        {/* Reorder controls — visible but subdued, appear more clearly on hover */}
        <div
          className="flex flex-col items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
          onClick={stop}
        >
          <button
            type="button"
            onClick={() => onMove(item, -1)}
            data-testid={`template-move-up-${item.id}`}
            disabled={idx === 0}
            className="p-1 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 disabled:opacity-30"
            title="Move up"
          >
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => onMove(item, 1)}
            data-testid={`template-move-down-${item.id}`}
            disabled={idx === siblingCount - 1}
            className="p-1 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 disabled:opacity-30"
            title="Move down"
          >
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>

        {/* Action-type icon medallion — the color instantly signals "what type
            of step" this is at a glance without reading the label. */}
        <div className="w-10 h-10 rounded-sm bg-[#6B0F1A]/8 border border-[#6B0F1A]/15 flex items-center justify-center shrink-0">
          <ActionIcon className="w-4 h-4 text-[#6B0F1A]" strokeWidth={1.75} />
        </div>

        {/* Title + at-a-glance metadata */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-mono-tag text-[9px] text-[#78584B]">
              {ACTION_META[item.action_type]?.label.toUpperCase()}
            </span>
            {!item.is_active && (
              <span className="font-mono-tag text-[9px] text-[#6B0F1A] inline-flex items-center gap-1">
                <EyeOff className="w-2.5 h-2.5" strokeWidth={2} /> HIDDEN
              </span>
            )}
            {needsAttention && (
              <span
                className="font-mono-tag text-[9px] text-[#B08D57] inline-flex items-center gap-1"
                data-testid={`template-needs-url-${item.id}`}
                title="This step is active but has no URL configured — photographers will see 'LINK COMING SOON'."
              >
                <AlertCircle className="w-2.5 h-2.5" strokeWidth={2} /> NEEDS URL
              </span>
            )}
          </div>
          <h3 className="font-display text-base font-semibold text-[#1A0F0A] leading-snug truncate">
            {item.title}
          </h3>
          {item.description && !isExpanded && (
            <p className="text-xs text-[#78584B] mt-1 truncate">{item.description}</p>
          )}
        </div>

        {/* Config-glance chips: URL, primary label, secondary button */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          {hasUrl && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6B0F1A]/8 text-[#6B0F1A] font-mono-tag text-[9px]"
              title={item.external_url}
              data-testid={`template-glance-url-${item.id}`}
            >
              <Link2 className="w-2.5 h-2.5" strokeWidth={2} /> URL
            </span>
          )}
          {hasCustomButtons && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6B0F1A]/8 text-[#6B0F1A] font-mono-tag text-[9px]"
              title={`${item.buttons.length} custom button(s) configured`}
              data-testid={`template-glance-custom-buttons-${item.id}`}
            >
              <MousePointerClick className="w-2.5 h-2.5" strokeWidth={2} />
              {item.buttons.length} BTN
            </span>
          )}
          {!hasCustomButtons && hasPrimaryLabel && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6B0F1A]/8 text-[#6B0F1A] font-mono-tag text-[9px]"
              title={`Primary: ${item.primary_label}`}
            >
              <MousePointerClick className="w-2.5 h-2.5" strokeWidth={2} /> 1
            </span>
          )}
          {!hasCustomButtons && hasSecondary && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#B08D57]/15 text-[#B08D57] font-mono-tag text-[9px]"
              title={`Secondary: ${item.secondary_label} → ${item.secondary_url}`}
            >
              <MousePointerClick className="w-2.5 h-2.5" strokeWidth={2} /> 2
            </span>
          )}
        </div>

        <ChevronDown
          className={`w-4 h-4 text-[#78584B] shrink-0 transition-transform duration-300 ${isExpanded ? "rotate-180 text-[#6B0F1A]" : ""}`}
          strokeWidth={2}
        />
      </div>

      {/* EXPANDED PANEL — smooth height/opacity animation, single source of edit */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-[#1A0F0A]/10 bg-[#FBF4E4]/40 overflow-hidden"
          >
            <div className="p-5 space-y-5">
              {isEditing ? (
                <ItemForm
                  value={editDraft}
                  onChange={onEditDraftChange}
                  onSubmit={onSubmitEdit}
                  onCancel={onCancelEdit}
                  submitting={savingEdit}
                  isNew={false}
                />
              ) : (
                <>
                  {item.description && (
                    <p className="text-sm text-[#4D3F35] leading-relaxed">
                      {item.description}
                    </p>
                  )}

                  {/* Live "photographer sees this" preview — makes the effect of
                      admin edits tangible without switching to the photographer
                      view. When the admin has configured a `buttons` array,
                      that becomes the ONLY source of truth (matches
                      ChecklistCard behavior). Otherwise fall back to the
                      legacy primary/secondary/auto-upload rendering. */}
                  <div>
                    <div className="font-mono-tag text-[10px] text-[#78584B] mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" strokeWidth={2} />
                      PHOTOGRAPHER PREVIEW
                    </div>
                    <div className="rounded-sm border border-[#1A0F0A]/10 bg-white p-4 flex flex-wrap items-center gap-3">
                      <div className="w-4 h-4 rounded-sm border border-[#1A0F0A]/25 shrink-0" />
                      <div className="flex-1 min-w-[160px]">
                        <div className="font-display text-sm font-semibold text-[#1A0F0A]">
                          {item.title}
                        </div>
                      </div>
                      {Array.isArray(item.buttons) && item.buttons.length > 0 ? (
                        item.buttons.map((btn, i) => {
                          const BIcon = iconForTitle(btn.title);
                          return (
                            <span
                              key={i}
                              className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-sm text-[11px] font-bold tracking-wide ${buttonColorClasses(btn.color)}`}
                            >
                              <BIcon className="w-3 h-3" strokeWidth={2} />
                              {btn.title || "(untitled)"}
                            </span>
                          );
                        })
                      ) : (
                        <>
                          {item.action_type !== "schedule" && (
                            <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-[#6B0F1A] text-[#F5EBD9] text-[11px] font-bold tracking-wide">
                              <ActionIcon className="w-3 h-3" strokeWidth={2} />
                              {primaryButtonText}
                            </span>
                          )}
                          {(item.action_type === "upload" || item.action_type === "sign") && (
                            <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-transparent border border-[#6B0F1A]/40 text-[#6B0F1A] text-[11px] font-bold tracking-wide">
                              Upload
                            </span>
                          )}
                          {hasSecondary && (
                            <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-sm bg-transparent border border-[#6B0F1A]/40 text-[#6B0F1A] text-[11px] font-bold tracking-wide">
                              {item.secondary_label}
                            </span>
                          )}
                          {item.action_type === "schedule" && (
                            <span className="font-mono-tag text-[10px] text-[#78584B]">
                              Inline Calendly widget
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* URL row — hide when custom buttons are configured. The
                      `external_url` field is a legacy fallback used only when
                      the item has no `buttons` array; showing it alongside
                      custom buttons is misleading because the buttons own the
                      real destinations. When buttons exist, we list each
                      button's URL beneath so admins see exactly what the
                      photographer will click on. */}
                  {!hasCustomButtons && item.external_url && (
                    <div>
                      <div className="font-mono-tag text-[10px] text-[#78584B] mb-1.5">
                        {item.action_type === "schedule" ? "CALENDLY URL" : "EXTERNAL URL"}
                      </div>
                      <a
                        href={item.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`template-url-${item.id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-[#6B0F1A] hover:text-[#8A1424] break-all"
                        onClick={stop}
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={2} />
                        <span className="truncate">{item.external_url}</span>
                      </a>
                    </div>
                  )}

                  {hasCustomButtons && item.buttons.some((b) => b.url) && (
                    <div data-testid={`template-button-urls-${item.id}`}>
                      <div className="font-mono-tag text-[10px] text-[#78584B] mb-1.5">
                        BUTTON URLS
                      </div>
                      <ul className="space-y-1">
                        {item.buttons.map((btn, i) => (
                          <li key={btn._key ?? i} className="flex items-baseline gap-2 text-xs">
                            <span className="font-mono-tag text-[10px] text-[#78584B] shrink-0 min-w-[60px] truncate">
                              {(btn.title || "—").toUpperCase()}
                            </span>
                            {btn.url ? (
                              <a
                                href={btn.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[#6B0F1A] hover:text-[#8A1424] break-all"
                                onClick={stop}
                              >
                                <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={2} />
                                <span className="truncate">{btn.url}</span>
                              </a>
                            ) : (
                              <span className="text-[#78584B] italic">
                                {btn.action === "upload" ? "(upload — no URL)" : "(no URL set)"}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action bar — labeled buttons so the affordance is obvious */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1A0F0A]/10">
                    <Button
                      onClick={(e) => { stop(e); onStartEdit(item); }}
                      data-testid={`template-edit-btn-${item.id}`}
                      className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-xs tracking-wide rounded-sm h-8"
                    >
                      <Pencil className="w-3 h-3 mr-1.5" strokeWidth={2} />
                      Edit
                    </Button>
                    <Button
                      onClick={(e) => { stop(e); onToggleActive(item); }}
                      data-testid={`template-toggle-active-${item.id}`}
                      variant="ghost"
                      className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/30 font-bold text-xs tracking-wide h-8"
                    >
                      {item.is_active ? (
                        <><EyeOff className="w-3 h-3 mr-1.5" strokeWidth={2} /> Hide</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1.5" strokeWidth={2} /> Show</>
                      )}
                    </Button>

                    {/* Copy this item into another role's checklist. Opens a
                        small dropdown listing the OTHER two roles (never
                        the current one — that would clutter and mislead).
                        Fires the async POST, toasts, and the parent
                        prepends the new item if the admin is viewing the
                        destination tab. */}
                    {onDuplicateTo && (() => {
                      const others = ["photographer", "pilot", "employee"].filter(
                        (r) => r !== (activeRole || item.role || "photographer"),
                      );
                      const labels = { photographer: "Photographer", pilot: "Pilot", employee: "Employee" };
                      return (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={stop}>
                            <Button
                              data-testid={`template-duplicate-btn-${item.id}`}
                              variant="ghost"
                              className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/30 font-bold text-xs tracking-wide h-8"
                            >
                              <Copy className="w-3 h-3 mr-1.5" strokeWidth={2} />
                              Copy to
                              <ChevronDown className="w-3 h-3 ml-1" strokeWidth={2} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" onClick={stop}>
                            {others.map((r) => (
                              <DropdownMenuItem
                                key={r}
                                data-testid={`template-duplicate-to-${r}-${item.id}`}
                                onClick={(e) => { stop(e); onDuplicateTo(item, r); }}
                                className="cursor-pointer"
                              >
                                {labels[r]} checklist
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()}
                    <Button
                      onClick={(e) => { stop(e); onRemove(item); }}
                      data-testid={`template-delete-btn-${item.id}`}
                      variant="ghost"
                      className="text-[#6B0F1A] hover:bg-[#6B0F1A]/8 border border-[#6B0F1A]/30 font-bold text-xs tracking-wide h-8 ml-auto"
                    >
                      <Trash2 className="w-3 h-3 mr-1.5" strokeWidth={2} />
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function TemplatesAdmin() {
  // Active checklist tab. All state below is scoped to this role — switching
  // tabs re-loads items, resets search/phase filters + collapsed accordion,
  // and stamps freshly-created items with this role.
  const [activeRole, setActiveRole] = useState("photographer");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft("photographer"));
  const [savingNew, setSavingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Apple-style single-expand accordion. `null` = everything collapsed.
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  // Collapsed phases — admins can hide entire phases they're done configuring
  // to focus on the section they're actively editing. Persisted in-memory only.
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());

  const load = async (role = activeRole) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/template-items?role=${role}`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load checklist items.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(activeRole); }, [activeRole]);

  const switchRole = (role) => {
    if (role === activeRole) return;
    // Discard in-flight drafts on tab switch so we don't accidentally save a
    // photographer draft into the pilot checklist.
    setCreating(false);
    setEditingId(null);
    setEditDraft(null);
    setExpandedId(null);
    setSearchQuery("");
    setPhaseFilter("all");
    setCollapsedPhases(new Set());
    setActiveRole(role);
  };

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((it) => {
      if (phaseFilter !== "all" && it.phase !== phaseFilter) return false;
      if (!q) return true;
      return (
        (it.title || "").toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q) ||
        (it.external_url || "").toLowerCase().includes(q)
      );
    });
  }, [items, searchQuery, phaseFilter]);

  const grouped = useMemo(() => {
    // Initialize buckets for every known phase so we can render empty sections.
    const g = {};
    for (const k of Object.keys(PHASES)) g[k] = [];
    for (const it of filteredItems) (g[it.phase] || (g[it.phase] = [])).push(it);
    for (const k of Object.keys(g)) g[k].sort((a, b) => a.order - b.order);
    return g;
  }, [filteredItems]);

  const metrics = useMemo(() => {
    const active = items.filter((i) => i.is_active).length;
    const hidden = items.length - active;
    const needsUrl = items.filter(
      (i) => i.is_active && !["upload", "exam"].includes(i.action_type) && !i.external_url
    ).length;
    return { total: items.length, active, hidden, needsUrl };
  }, [items]);

  const buildPayload = (source) => {
    // Only send `buttons` if the admin actually configured something —
    // sending `null` clears the field, preserving legacy items whose primary
    // CTA still lives in `external_url` / `primary_label`.
    const btns = Array.isArray(source.buttons) ? source.buttons : [];
    const cleanedButtons = btns
      .map((b) => ({
        title: (b?.title || "").trim(),
        color: b?.color || "primary",
        action: b?.action || "url",
        url: stripWwwFromUrl(b?.url) || null,
      }))
      .filter((b) => b.title.length > 0);
    return {
      role: source.role || activeRole,
      title: source.title,
      description: source.description || null,
      phase: source.phase,
      action_type: source.action_type,
      external_url: stripWwwFromUrl(source.external_url) || null,
      // Empty strings should clear the field, not leave stale prior values.
      primary_label: source.primary_label || null,
      secondary_label: source.secondary_label || null,
      secondary_url: stripWwwFromUrl(source.secondary_url) || null,
      buttons: cleanedButtons.length > 0 ? cleanedButtons : null,
    };
  };

  const startCreate = () => {
    setDraft(emptyDraft(activeRole));
    setCreating(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setSavingNew(true);
    try {
      const phaseMax = Math.max(
        0,
        ...items.filter((i) => i.phase === draft.phase).map((i) => Number(i.order) || 0)
      );
      const payload = { ...buildPayload(draft), order: phaseMax + 10 };
      const { data } = await api.post("/admin/template-items", payload);
      setItems((prev) => [...prev, data]);
      toast.success("Item created.");
      setCreating(false);
      setDraft(emptyDraft(activeRole));
      // Reveal the newly-created row so the admin instantly sees the result.
      setExpandedId(data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join("; ")
        : (typeof detail === "string" ? detail : "Could not create item.");
      toast.error(msg);
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditDraft({ ...it });
    // Guarantee the expanded panel is showing so the form is visible.
    setExpandedId(it.id);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editDraft) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...buildPayload(editDraft),
        is_active: editDraft.is_active,
        order: Number(editDraft.order) || 0,
      };
      const { data } = await api.patch(`/admin/template-items/${editDraft.id}`, payload);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success("Item updated.");
      setEditingId(null);
      setEditDraft(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join("; ")
        : (typeof detail === "string" ? detail : "Could not update item.");
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const swapOrder = async (item, delta) => {
    // Find neighbor within the item's phase (using the FULL items list, not filtered,
    // so search/filter never breaks reorder correctness).
    const list = items
      .filter((x) => x.phase === item.phase)
      .sort((a, b) => a.order - b.order);
    const idx = list.findIndex((x) => x.id === item.id);
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    const a = list[idx];
    const b = list[target];
    setItems((prev) =>
      prev.map((x) => {
        if (x.id === a.id) return { ...x, order: b.order };
        if (x.id === b.id) return { ...x, order: a.order };
        return x;
      })
    );
    try {
      await Promise.all([
        api.patch(`/admin/template-items/${a.id}`, { order: b.order }),
        api.patch(`/admin/template-items/${b.id}`, { order: a.order }),
      ]);
    } catch {
      toast.error("Could not reorder.");
      await load();
    }
  };

  const toggleActive = async (it) => {
    try {
      const { data } = await api.patch(`/admin/template-items/${it.id}`, { is_active: !it.is_active });
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.is_active ? "Item shown to photographers." : "Item hidden from photographers.");
    } catch {
      toast.error("Could not toggle item.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? This also removes every photographer's progress for this item.`)) return;
    try {
      await api.delete(`/admin/template-items/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Item deleted.");
    } catch {
      toast.error("Could not delete item.");
    }
  };

  // Copy an existing checklist item into another role's checklist. Great
  // for bootstrapping Pilot / Employee onboarding from the working
  // Photographer flow — admin picks the target role from the item's own
  // action bar, we POST /duplicate, and toast on success. If the target
  // role is the currently-active tab we prepend the copy to the visible
  // list so admins can immediately edit.
  const duplicateTo = async (item, targetRole) => {
    try {
      const { data } = await api.post(`/admin/template-items/${item.id}/duplicate`, {
        target_role: targetRole,
      });
      const roleLabel = targetRole.charAt(0).toUpperCase() + targetRole.slice(1);
      toast.success(`Copied "${item.title}" to ${roleLabel} checklist.`);
      // If we're viewing the destination tab, insert immediately; otherwise
      // it's already saved server-side and will show up when they switch.
      if (targetRole === activeRole) {
        setItems((prev) => [data, ...prev]);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not duplicate item.";
      toast.error(typeof msg === "string" ? msg : "Could not duplicate item.");
    }
  };

  const toggleExpanded = (id) => {
    // Never collapse a row that's actively being edited — the admin would
    // lose their form input mid-typing. This makes the accordion feel safe.
    if (editingId && editingId === expandedId && id === expandedId) return;
    setExpandedId((cur) => (cur === id ? null : id));
  };

  const togglePhase = (key) => {
    setCollapsedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return <ApertureLoader label="Loading checklist…" />;
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10" data-testid="templates-admin">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Onboarding Checklist
          </h1>
          <p className="text-[#4D3F35] mt-3 leading-relaxed">
            {ROLE_HEADLINE[activeRole]}
          </p>
        </div>
        {!creating && (
          <Button
            onClick={startCreate}
            data-testid="new-item-btn"
            className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm btn-primary-glow"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2.25} />
            New item
          </Button>
        )}
      </div>

      {/* ROLE TAB BAR — each role owns an isolated checklist. Switching a
          tab reloads items scoped to that role, blanks search/phase filters,
          and stamps any new items with that role. */}
      <div
        role="tablist"
        aria-label="Checklist role"
        className="flex flex-wrap gap-2 mb-8"
        data-testid="templates-role-tabs"
      >
        {CHECKLIST_ROLES.map((r) => {
          const Icon = r.icon;
          const isActive = activeRole === r.key;
          return (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => switchRole(r.key)}
              data-testid={`templates-role-tab-${r.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border transition-all text-left ${
                isActive
                  ? "border-[#6B0F1A] bg-[#6B0F1A]/6 text-[#6B0F1A] shadow-[inset_0_0_0_1px_rgba(107,15,26,0.28)]"
                  : "border-[#1A0F0A]/12 bg-white text-[#1A0F0A] hover:border-[#1A0F0A]/30"
              }`}
            >
              <Icon
                className={`w-4 h-4 ${isActive ? "text-[#6B0F1A]" : "text-[#78584B]"}`}
                strokeWidth={2}
              />
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-bold tracking-tight">{r.label}</span>
                <span
                  className={`font-mono-tag text-[9px] tracking-[0.14em] ${
                    isActive ? "text-[#6B0F1A]/70" : "text-[#78584B]"
                  }`}
                >
                  {r.blurb.toUpperCase()}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* METRIC CHIPS + WARNING */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="templates-metrics">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-[#1A0F0A]/10 font-mono-tag text-[10px] text-[#4D3F35]">
          <ListChecks className="w-3 h-3 text-[#6B0F1A]" strokeWidth={2} />
          {metrics.total} TOTAL
        </span>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-[#1A0F0A]/10 font-mono-tag text-[10px] text-[#4D3F35]">
          <Eye className="w-3 h-3 text-[#6B0F1A]" strokeWidth={2} />
          {metrics.active} ACTIVE
        </span>
        {metrics.hidden > 0 && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-[#1A0F0A]/10 font-mono-tag text-[10px] text-[#78584B]">
            <EyeOff className="w-3 h-3" strokeWidth={2} />
            {metrics.hidden} HIDDEN
          </span>
        )}
        {metrics.needsUrl > 0 && (
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#B08D57]/10 border border-[#B08D57]/30 font-mono-tag text-[10px] text-[#B08D57]"
            data-testid="templates-needs-url-count"
          >
            <AlertCircle className="w-3 h-3" strokeWidth={2} />
            {metrics.needsUrl} NEEDS URL
          </span>
        )}
      </div>

      {/* SEARCH + FILTER BAR — sticky-ish; helps admins jump to the item they care about */}
      <div className="flex flex-wrap items-center gap-3 mb-8 card-surface rounded-sm p-3" data-testid="templates-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#78584B] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, description, or URL…"
            data-testid="templates-search-input"
            className="pl-9 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] text-sm h-9"
          />
        </div>
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger
            data-testid="templates-phase-filter"
            className="w-[180px] bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm h-9"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A]">
            <SelectItem value="all">All phases</SelectItem>
            {Object.entries(PHASES).map(([k, meta]) => (
              <SelectItem key={k} value={k}>{meta.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(searchQuery || phaseFilter !== "all") && (
          <Button
            variant="ghost"
            onClick={() => { setSearchQuery(""); setPhaseFilter("all"); }}
            data-testid="templates-clear-filter"
            className="text-[#78584B] hover:text-[#6B0F1A] font-mono-tag text-[10px] h-9"
          >
            <X className="w-3 h-3 mr-1" strokeWidth={2} />
            Clear
          </Button>
        )}
      </div>

      {/* NEW ITEM FORM (inline) */}
      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div
              className="card-surface rounded-sm p-6 mb-8 border-[#6B0F1A]/40 shadow-[0_10px_30px_-15px_rgba(107,15,26,0.4)]"
              data-testid="new-item-panel"
            >
              <div className="font-mono-tag text-[10px] text-[#6B0F1A] mb-4 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" strokeWidth={2} /> NEW CHECKLIST ITEM
              </div>
              <ItemForm
                value={draft}
                onChange={setDraft}
                onSubmit={submitCreate}
                onCancel={() => setCreating(false)}
                submitting={savingNew}
                isNew
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PHASES */}
      {Object.entries(PHASES).map(([phaseKey, meta]) => {
        const list = grouped[phaseKey] || [];
        // Hide entire phase section when filter is active AND phase is empty
        // after filtering — otherwise we get a wall of "no items" placeholders.
        const isFiltered = searchQuery || phaseFilter !== "all";
        if (isFiltered && list.length === 0) return null;
        const PhaseIcon = PHASE_ICON[phaseKey] || FileText;
        const isPhaseCollapsed = collapsedPhases.has(phaseKey);
        const activeCount = list.filter((i) => i.is_active).length;
        return (
          <section key={phaseKey} className="mb-8" data-testid={`templates-phase-${phaseKey}`}>
            <button
              type="button"
              onClick={() => togglePhase(phaseKey)}
              data-testid={`templates-phase-toggle-${phaseKey}`}
              className="w-full flex items-baseline justify-between mb-4 pb-3 border-b border-[#1A0F0A]/12 group text-left"
            >
              <div className="flex items-center gap-3">
                <PhaseIcon className="w-4 h-4 text-[#6B0F1A]" strokeWidth={1.75} />
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight text-[#1A0F0A]">{meta.label}</h2>
                  <p className="text-xs text-[#78584B] mt-0.5">{meta.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-tag text-[10px] text-[#78584B]">
                  {activeCount}/{list.length} ACTIVE
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-[#78584B] group-hover:text-[#6B0F1A] transition-transform ${isPhaseCollapsed ? "-rotate-90" : ""}`}
                  strokeWidth={2}
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {!isPhaseCollapsed && (
                <motion.div
                  key="phase-body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  {list.length === 0 ? (
                    <div className="text-sm text-[#78584B] py-6 italic">No items in this phase yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {list.map((it, idx) => (
                        <TemplateRow
                          key={it.id}
                          item={it}
                          idx={idx}
                          siblingCount={list.length}
                          isExpanded={expandedId === it.id}
                          isEditing={editingId === it.id}
                          onToggleExpanded={toggleExpanded}
                          onStartEdit={startEdit}
                          onCancelEdit={() => { setEditingId(null); setEditDraft(null); }}
                          onSubmitEdit={submitEdit}
                          editDraft={editDraft}
                          onEditDraftChange={setEditDraft}
                          savingEdit={savingEdit}
                          onMove={swapOrder}
                          onToggleActive={toggleActive}
                          onRemove={remove}
                          onDuplicateTo={duplicateTo}
                          activeRole={activeRole}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        );
      })}

      {items.length === 0 && !creating && (
        <div className="card-surface rounded-sm p-10 text-center" data-testid="templates-empty">
          <ListChecks className="w-8 h-8 mx-auto text-[#1A0F0A]/25 mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#78584B]">No checklist items yet. Click "New item" to add the first one.</p>
        </div>
      )}

      {items.length > 0 && filteredItems.length === 0 && (
        <div className="card-surface rounded-sm p-10 text-center" data-testid="templates-no-matches">
          <Search className="w-8 h-8 mx-auto text-[#1A0F0A]/25 mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#78584B]">
            No items match your search. Try clearing the filter.
          </p>
        </div>
      )}
    </div>
  );
}
